import { Component, OnInit, OnDestroy, Inject, Optional, Input, Output, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, of } from 'rxjs';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { ScheduledActivity, ActivityType } from '../../interfaces/activity-schedule.interface';
import { ActivityBookingComponent } from '../activity-booking/activity-booking.component';
import { NotificationService } from 'src/app/services/notification.service';

export interface ActivityDetailsData {
  activity: ScheduledActivity;
  mode?: 'view' | 'modal';
}

@Component({
  selector: 'app-activity-details',
  templateUrl: './activity-details.component.html',
  styleUrls: ['./activity-details.component.scss']
})
export class ActivityDetailsComponent implements OnInit, OnDestroy {
  @Input() activity: ScheduledActivity | null = null;
  @Output() activityUpdated = new EventEmitter<ScheduledActivity>();
  @Output() activityDeleted = new EventEmitter<number>();

  private destroy$ = new Subject<void>();
  
  // State
  isLoading = false;
  isUpdatingStatus = false;
  activityTypes: ActivityType[] = [];
  mode: 'view' | 'modal' = 'view';

  // Status options with colors
  statusOptions = [
    { value: 'scheduled', label: 'Programat', color: '#2196F3', icon: 'schedule' },
    { value: 'completed', label: 'Finalizat', color: '#4CAF50', icon: 'check_circle' },
    { value: 'cancelled', label: 'Anulat', color: '#F44336', icon: 'cancel' },
    { value: 'rescheduled', label: 'Reprogramat', color: '#FF9800', icon: 'event_repeat' },
    { value: 'no_show', label: 'Absent', color: '#9E9E9E', icon: 'person_remove' }
  ];

  constructor(
    private activityScheduleService: ActivityScheduleService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    @Optional() private dialogRef: MatDialogRef<ActivityDetailsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ActivityDetailsData
  ) {
    if (data) {
      this.activity = data.activity;
      this.mode = data.mode || 'modal';
    }
  }

  ngOnInit(): void {
    this.loadActivityTypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadActivityTypes(): void {
    this.activityScheduleService.getActivityTypes().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (types) => {
        this.activityTypes = types;
      },
      error: (error) => {
        console.error('Error loading activity types:', error);
      }
    });
  }

  // UI Helpers
  getStatusLabel(status: string): string {
    const statusOption = this.statusOptions.find(s => s.value === status);
    return statusOption?.label || status;
  }

  getStatusColor(status: string): string {
    const statusOption = this.statusOptions.find(s => s.value === status);
    return statusOption?.color || '#2196F3';
  }

  getStatusIcon(status: string): string {
    const statusOption = this.statusOptions.find(s => s.value === status);
    return statusOption?.icon || 'help';
  }

  getActivityTypeLabel(typeId: number): string {
    const type = this.activityTypes.find(t => t.id === typeId);
    return type?.name || `Tip ${typeId}`;
  }

  formatTime(time: string): string {
    if (!time) return 'N/A';
    return time.substring(0, 5); // HH:MM from HH:MM:SS
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('ro-RO');
  }

  formatDateTime(date: string, time: string): string {
    if (!date || !time) return 'N/A';
    const dateTime = new Date(`${date}T${time}`);
    return dateTime.toLocaleString('ro-RO');
  }

  getDuration(): string {
    if (!this.activity?.start_time || !this.activity?.end_time) return 'N/A';
    
    const start = this.timeToMinutes(this.activity.start_time);
    const end = this.timeToMinutes(this.activity.end_time);
    const duration = end - start;
    
    if (duration < 60) {
      return `${duration} minute`;
    } else {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      if (minutes > 0) {
        return `${hours}h ${minutes}min`;
      } else {
        return `${hours}h`;
      }
    }
  }

  private timeToMinutes(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  isPastActivity(): boolean {
    if (!this.activity?.scheduled_date || !this.activity?.end_time) return false;
    const activityDateTime = new Date(`${this.activity.scheduled_date}T${this.activity.end_time}`);
    return activityDateTime < new Date();
  }

  isTodayActivity(): boolean {
    if (!this.activity?.scheduled_date) return false;
    const today = new Date().toISOString().split('T')[0];
    return this.activity.scheduled_date === today;
  }

  getTimeUntilActivity(): string {
    if (!this.activity?.scheduled_date || !this.activity?.start_time) return '';
    
    const activityDateTime = new Date(`${this.activity.scheduled_date}T${this.activity.start_time}`);
    const now = new Date();
    const diffMs = activityDateTime.getTime() - now.getTime();
    
    if (diffMs < 0) return 'Trecut';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} zi${diffDays > 1 ? 'le' : ''}`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}min`;
    } else {
      return `${diffMinutes}min`;
    }
  }

  // Actions
  editActivity(): void {
    if (!this.activity) return;

    const dialogRef = this.dialog.open(ActivityBookingComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: {
        mode: 'edit',
        activityId: this.activity.id,
        prefilledData: this.activity
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.activity = result;
        this.activityUpdated.emit(result);
        this.notificationService.successSwal('Succes', 'Activitatea a fost actualizată cu succes!');
      }
    });
  }

  rescheduleActivity(): void {
    if (!this.activity) return;

    const dialogRef = this.dialog.open(ActivityBookingComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: {
        mode: 'reschedule',
        activityId: this.activity.id,
        prefilledData: {
          ...this.activity,
          date: undefined, // Force user to select new date
          startTime: undefined
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.activity = result;
        this.activityUpdated.emit(result);
        this.notificationService.successSwal('Succes', 'Activitatea a fost reprogramată cu succes!');
      }
    });
  }

  duplicateActivity(): void {
    if (!this.activity) return;

    const dialogRef = this.dialog.open(ActivityBookingComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: {
        mode: 'create',
        prefilledData: {
          ...this.activity,
          id: undefined, // Remove ID for duplication
          title: `${this.activity.title} (Copie)`,
          date: undefined, // Force user to select date
          startTime: undefined
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.notificationService.successSwal('Succes', 'Activitatea a fost duplicată cu succes!');
        if (this.mode === 'modal' && this.dialogRef) {
          this.dialogRef.close(result);
        }
      }
    });
  }

  updateActivityStatus(newStatus: string): void {
    if (!this.activity || this.activity.status === newStatus) return;

    this.isUpdatingStatus = true;
    
    this.activityScheduleService.updateActivityStatus(this.activity.id, { status: newStatus as any }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedActivity) => {
        this.activity = updatedActivity;
        this.isUpdatingStatus = false;
        this.activityUpdated.emit(updatedActivity);
        
        const statusLabel = this.getStatusLabel(newStatus);
        this.notificationService.successSwal('Succes', `Activitatea a fost marcată ca "${statusLabel}"`);
      },
      error: (error) => {
        this.isUpdatingStatus = false;
        console.error('Error updating activity status:', error);
        this.notificationService.warningSwal('Eroare', 'Eroare la actualizarea statusului activității', 'error');
      }
    });
  }

  deleteActivity(): void {
    if (!this.activity) return;

    this.notificationService.warningSwal(
      'Confirmare',
      `Sunteți sigur că doriți să ștergeți activitatea "${this.activity.title}"?`,
      'warning'
    ).then((result) => {
      if (result.isConfirmed && this.activity) {
        // Simulate deletion for now
        of(true).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.activityDeleted.emit(this.activity!.id);
            this.notificationService.successSwal('Succes', 'Activitatea a fost ștearsă cu succes!');
            
            if (this.mode === 'modal' && this.dialogRef) {
              this.dialogRef.close('deleted');
            }
          },
          error: (error: any) => {
            console.error('Error deleting activity:', error);
            this.notificationService.warningSwal('Eroare', 'Eroare la ștergerea activității', 'error');
          }
        });
      }
    });
  }

  addToHistory(): void {
    if (!this.activity) return;

    this.notificationService.warningSwal(
      'Confirmare',
      'Doriți să adăugați această activitate la istoricul cererii?',
      'info'
    ).then((result) => {
      if (result.isConfirmed && this.activity) {
        // Simulate adding to history for now
        of(true).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.notificationService.successSwal('Succes', 'Activitatea a fost adăugată la istoricul cererii!');
          },
          error: (error: any) => {
            console.error('Error adding activity to history:', error);
            this.notificationService.warningSwal('Eroare', 'Eroare la adăugarea în istoric', 'error');
          }
        });
      }
    });
  }

  closeModal(): void {
    if (this.mode === 'modal' && this.dialogRef) {
      this.dialogRef.close();
    }
  }

  // Quick status update methods
  markCompleted(): void {
    this.updateActivityStatus('completed');
  }

  markCancelled(): void {
    this.updateActivityStatus('cancelled');
  }

  markNoShow(): void {
    this.updateActivityStatus('no_show');
  }

  canEditActivity(): boolean {
    return !this.isPastActivity() || this.activity?.status !== 'completed';
  }

  canReschedule(): boolean {
    return this.activity?.status === 'scheduled' || this.activity?.status === 'rescheduled';
  }

  canMarkCompleted(): boolean {
    return this.activity?.status === 'scheduled' && this.isTodayActivity();
  }

  canCancel(): boolean {
    return this.activity?.status === 'scheduled';
  }
}